import type { ColumnDef } from '@tanstack/react-table';

import type {
  ITestAccuracyDay,
  TestAccuracySection,
} from '@/feature/testAccuracy/types/testAccuracy';
import {
  formatDisplayDate,
  formatRate,
  getVerificationPassRate,
  rateTone,
  rateToneClass,
} from '@/feature/testAccuracy/utils/testAccuracyFormat';

const noTruncateMeta = { noTruncate: true } as const;
const rightMeta = { ...noTruncateMeta, align: 'right' as const };

const dateMeta = { ...noTruncateMeta, width: '155px' };
const metricMeta = { ...rightMeta, width: '80px' };
const compactMetricMeta = { ...rightMeta, width: '72px' };
const neverPassedMeta = { ...rightMeta, width: '120px' };
const efficiencyMeta = { ...rightMeta, width: '108px' };

const RateDisplay = ({ rate }: { rate?: number | null }) => {
  const tone = rateTone(rate);
  if (rate === null || rate === undefined) {
    return <span className='text-gray-400'>—</span>;
  }
  return (
    <span className={`font-semibold tabular-nums ${rateToneClass(tone)}`}>{formatRate(rate)}</span>
  );
};

const metricCell = (value: string | number, className?: string) => (
  <span className={`tabular-nums ${className ?? ''}`}>{value}</span>
);

export function getTestAccuracyDailyColumns(
  section: TestAccuracySection
): ColumnDef<ITestAccuracyDay>[] {
  const dateColumn: ColumnDef<ITestAccuracyDay> = {
    accessorKey: 'date',
    header: 'Date',
    meta: dateMeta,
    cell: ({ getValue }) => (
      <span className='font-medium text-gray-900'>{formatDisplayDate(getValue() as string)}</span>
    ),
  };

  if (section === 'verification') {
    return [
      dateColumn,
      {
        id: 'total',
        header: 'Total',
        meta: metricMeta,
        accessorFn: (row) => row.verification?.total_executed,
        cell: ({ getValue }) => metricCell((getValue() as number | undefined) ?? '—'),
      },
      {
        id: 'first_pass',
        header: '1st',
        meta: compactMetricMeta,
        accessorFn: (row) => row.verification?.passed_1st_attempt,
        cell: ({ getValue }) =>
          metricCell((getValue() as number | undefined) ?? 0, 'text-emerald-700 font-semibold'),
      },
      {
        id: 'second_pass',
        header: '2nd',
        meta: compactMetricMeta,
        accessorFn: (row) => row.verification?.passed_2nd_attempt,
        cell: ({ getValue }) =>
          metricCell((getValue() as number | undefined) ?? 0, 'text-emerald-700 font-semibold'),
      },
      {
        id: 'third_plus_pass',
        header: '3rd+',
        meta: compactMetricMeta,
        accessorFn: (row) => row.verification?.passed_3rd_plus_attempt,
        cell: ({ getValue }) =>
          metricCell((getValue() as number | undefined) ?? 0, 'text-yellow-600 font-semibold'),
      },
      {
        id: 'never_passed',
        header: 'Never Passed',
        meta: neverPassedMeta,
        accessorFn: (row) => row.verification?.not_passed,
        cell: ({ getValue }) =>
          metricCell((getValue() as number | undefined) ?? 0, 'text-red-700 font-semibold'),
      },
      {
        id: 'efficiency',
        header: 'Efficiency %',
        meta: efficiencyMeta,
        accessorFn: (row) => getVerificationPassRate(row.verification),
        cell: ({ getValue }) => <RateDisplay rate={getValue() as number | null} />,
      },
    ];
  }

  return [
    dateColumn,
    {
      id: 'total',
      header: 'Total',
      meta: metricMeta,
      accessorFn: (row) => row.execution?.total_executed,
      cell: ({ getValue }) => metricCell((getValue() as number | undefined) ?? '—'),
    },
    {
      id: 'passed_after_verify',
      header: 'Passed',
      meta: compactMetricMeta,
      accessorFn: (row) => row.execution?.passed_after_first_verify,
      cell: ({ getValue }) =>
        metricCell((getValue() as number | undefined) ?? 0, 'text-emerald-700 font-semibold'),
    },
    {
      id: 'reverify',
      header: 'Passed After Re-verify',
      meta: { ...rightMeta, width: '175px' },
      accessorFn: (row) => row.execution?.required_reverification,
      cell: ({ getValue }) =>
        metricCell((getValue() as number | undefined) ?? 0, 'text-emerald-700 font-semibold'),
    },
    {
      id: 'never',
      header: 'Failed',
      meta: compactMetricMeta,
      accessorFn: (row) => row.execution?.never_passed_after_verify,
      cell: ({ getValue }) =>
        metricCell((getValue() as number | undefined) ?? 0, 'text-red-700 font-semibold'),
    },
    {
      id: 'regressed',
      header: 'Regressed',
      meta: { ...rightMeta, width: '100px' },
      accessorFn: (row) => row.execution?.regressed,
      cell: ({ getValue }) =>
        metricCell((getValue() as number | undefined) ?? 0, 'text-red-700 font-semibold'),
    },
  ];
}
